import { useMemo } from "react";

import { readState } from "@/__main__/app-state.mjs";
import { isRevivalSet, TRAIT_TRANSLATIONS } from "@/game-tft/constants.mjs";
import { getChampionsByTrait } from "@/game-tft/get-champions-by-trait.mjs";
import getFilters from "@/game-tft/get-stats-filters.mjs";
import { styleToColor } from "@/game-tft/get-style-to-color.mjs";
import StaticTFT from "@/game-tft/static.mjs";
import useSetByMatch from "@/game-tft/use-set-by-match.mjs";
import useSetByParam from "@/game-tft/use-set-by-param.mjs";
import { getLocale } from "@/util/i18n-helper.mjs";
import { useRoute } from "@/util/router-hooks.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

export function useTraits(src = "match", set) {
  const state = useSnapshot(readState);
  const selectedSet = set
    ? set
    : src === "match"
      ? useSetByMatch()
      : useSetByParam(true);
  return state.tft[selectedSet]?.traits ?? {};
}

export function useTraitsByParams(page, isFutureSet = false) {
  const { searchParams } = useRoute();
  const set = useSetByParam(true);
  const traitFilters = getFilters(
    searchParams,
    page,
    isFutureSet,
    isRevivalSet(set),
  );
  const state = useSnapshot(readState);
  const traitsStats = useMemo(() => {
    const data = state.tft.stats.traits?.[traitFilters]?.[set];
    if (data?.length >= 5) {
      const result = [];
      data.map((trait) =>
        result.push({
          ...trait,
          apiKey: trait.trait_api_name,
          key: StaticTFT.getTraitKey(trait.trait_api_name, set),
          style: trait.style,
          color: styleToColor(trait.style),
          count: trait.trait_count,
        }),
      );
      return { result: result, data: data };
    }
    return typeof data !== "undefined"
      ? { result: [], data: [] }
      : { result: data, data: data };
  }, [state.tft.stats.traits, traitFilters, set]);
  return traitsStats;
}

export function translateTraits(t, value, set) {
  const locale = getLocale();
  const apiKey = set && StaticTFT.getTraitApiKey(value, set);

  const parsed = value?.toLowerCase().replace(/[\s-/]+/g, "");
  const translatedTrait = TRAIT_TRANSLATIONS[parsed]
    ? t(TRAIT_TRANSLATIONS[parsed])
    : readState.tft[set]?.traits?.[apiKey]?.name || value;

  if (!set || !readState.tft[set]?.localizedTraits) {
    return translatedTrait;
  }

  const traitNameFromStaticData =
    readState.tft[set]?.localizedTraits?.[locale]?.[apiKey]?.name;
  const result = traitNameFromStaticData
    ? traitNameFromStaticData
    : translatedTrait;

  return result;
}

export function getTraitInfo(trait, set, origins, classes) {
  if (!trait || !origins || !classes) return {};
  const traitKey = trait;
  const traitInfo = Object.keys(origins).includes(traitKey)
    ? { info: origins[traitKey], type: ["tft:traits:origins", "Origins"] }
    : { info: classes[traitKey], type: ["tft:traits:classes", "Classes"] };

  traitInfo.champions = getChampionsByTrait(
    traitKey,
    traitInfo.type[1].toLowerCase(),
    set,
  );
  return traitInfo;
}

export const useLocalizedTraits = (set) => {
  const locale = getLocale();
  const state = useSnapshot(readState);
  if (!set || !state.tft[set]?.localizedTraits) return {};
  const result = state.tft[set]?.localizedTraits?.[locale];
  return result && !(result instanceof Error) ? result : null;
};
